import React, { useState } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { CustomerRecord } from '../../CustomerSupport/types';
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell
} from '@/stories/Shadcn/Table/Table';
import { formatDateTime } from '@/utils/utils';
import { Maximize2, Minimize2, X } from 'react-feather';

interface CustomersPreviewTableProps {
	customers: CustomerRecord[];
	renderCustomerName: (customer: CustomerRecord) => string;
	t: (key: string, params?: any) => string;
}

const CustomersPreviewTable: React.FC<CustomersPreviewTableProps> = ({
	customers,
	renderCustomerName,
	t
}) => {
	const [isFullscreen, setIsFullscreen] = useState(false);

	if (customers.length === 0) return null;

	const formatCurrency = (value: string | number | undefined) => {
		if (!value) return '-';
		const num = typeof value === 'string' ? parseFloat(value) : value;
		return isNaN(num)
			? '-'
			: `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	};

	const formatDecimal = (value: string | number | undefined) => {
		if (!value) return '-';
		return value.toString();
	};

	const tableContent = (
		<>
			<div className="mb-3 flex items-center justify-between">
				<Typography size="md" weight="medium">
					{t('Preview')} ({customers.length} {t('CustomersFound')})
				</Typography>
				<button
					onClick={() => setIsFullscreen(!isFullscreen)}
					className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
					title={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
				>
					{isFullscreen ? (
						<>
							<Minimize2 size={18} />
							<span>Exit Fullscreen</span>
						</>
					) : (
						<>
							<Maximize2 size={18} />
							<span>Fullscreen</span>
						</>
					)}
				</button>
			</div>
			<div
				className={`${isFullscreen ? 'h-[calc(100vh-120px)]' : 'max-h-96'} overflow-x-auto overflow-y-auto border custom-scrollbar`}
			>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="min-w-[280px]">{t('ClientId')}</TableHead>
							<TableHead className="min-w-[140px]">{t('PhoneNumber')}</TableHead>
							<TableHead className="min-w-[200px]">{t('FullName')}</TableHead>
							<TableHead className="min-w-[220px]">{t('Email')}</TableHead>
							<TableHead className="min-w-[180px]">{t('StartedAt')}</TableHead>
							<TableHead className="min-w-[180px]">{t('ApprovedAt')}</TableHead>
							<TableHead className="min-w-[160px]">{t('OpenCurrentBalance')}</TableHead>
							<TableHead className="min-w-[160px]">{t('OpenOverdueBalance')}</TableHead>
							<TableHead className="min-w-[180px]">{t('ClosedMaxBalance')}</TableHead>
							<TableHead className="min-w-[140px]">{t('MonthlyPayment')}</TableHead>
							<TableHead className="min-w-[140px]">{t('DefaultScore')}</TableHead>
							<TableHead className="min-w-[100px]">{t('TotalTrials')}</TableHead>
							<TableHead className="min-w-[140px]">{t('MonthsOverdue')}</TableHead>
							<TableHead className="min-w-[160px]">{t('YearsInBureau')}</TableHead>
							<TableHead className="min-w-[180px]">{t('UtilizationPrePandemic')}</TableHead>
							<TableHead className="min-w-[140px]">{t('LastSixMonthsInquiries')}</TableHead>
							<TableHead className="min-w-[180px]">{t('UtilizationPostPandemic')}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{customers.map((customer, idx) => (
							<TableRow key={idx}>
								<TableCell className="font-mono text-xs">{customer.client_id}</TableCell>
								<TableCell>{customer.phone_number}</TableCell>
								<TableCell className="whitespace-normal max-w-48">
									{renderCustomerName(customer)}
								</TableCell>
								<TableCell className="text-xs">{customer.email || '-'}</TableCell>
								<TableCell className="text-xs">
									{formatDateTime(customer.started_at || '', 'es') || '-'}
								</TableCell>
								<TableCell className="text-xs">
									{formatDateTime(customer.approved_at || '', 'es') || '-'}
								</TableCell>
								<TableCell className="text-right">
									{formatCurrency(customer.open_current_balance)}
								</TableCell>
								<TableCell className="text-right">
									{formatCurrency(customer.open_overdue_balance)}
								</TableCell>
								<TableCell className="text-right">
									{formatCurrency(customer.closed_maximum_balance)}
								</TableCell>
								<TableCell className="text-right">
									{formatCurrency(customer.monthly_payment)}
								</TableCell>
								<TableCell className="text-right">
									{formatDecimal(customer.credit_bureau_default_score)}
								</TableCell>
								<TableCell className="text-center">{customer.total_trials ?? '-'}</TableCell>
								<TableCell className="text-center">{customer.months_overdue ?? '-'}</TableCell>
								<TableCell className="text-center">
									{customer.years_in_credit_bureau ?? '-'}
								</TableCell>
								<TableCell className="text-right">
									{formatDecimal(customer.utilization_pre_pandemic)}
								</TableCell>
								<TableCell className="text-center">
									{customer.last_six_months_inquiries ?? '-'}
								</TableCell>
								<TableCell className="text-right">
									{formatDecimal(customer.utilization_post_pandemic)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	);

	if (isFullscreen) {
		return (
			<div className="fixed inset-0 z-[9999] bg-white overflow-hidden flex flex-col">
				<div className="flex items-center justify-between px-6 py-4 border-b bg-white shadow-sm">
					<Typography type="display" size="sm">
						{t('Preview')} ({customers.length} {t('CustomersFound')})
					</Typography>
					<button
						onClick={() => setIsFullscreen(false)}
						className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
					>
						<X size={20} />
						<span>Close</span>
					</button>
				</div>
				<div className="flex-1 overflow-auto p-6 bg-gray-50">
					<div className="bg-white border rounded-lg overflow-hidden shadow-sm">
						<div className="overflow-x-auto custom-scrollbar">
							<Table>
								<TableHeader className="sticky top-0 bg-gray-50 z-10 shadow-sm">
									<TableRow>
										<TableHead className="min-w-[280px] whitespace-nowrap bg-gray-50">
											{t('ClientId')}
										</TableHead>
										<TableHead className="min-w-[140px] whitespace-nowrap bg-gray-50">
											{t('PhoneNumber')}
										</TableHead>
										<TableHead className="min-w-[200px] whitespace-nowrap bg-gray-50">
											{t('FullName')}
										</TableHead>
										<TableHead className="min-w-[220px] whitespace-nowrap bg-gray-50">
											{t('Email')}
										</TableHead>
										<TableHead className="min-w-[180px] whitespace-nowrap bg-gray-50">
											{t('StartedAt')}
										</TableHead>
										<TableHead className="min-w-[180px] whitespace-nowrap bg-gray-50">
											{t('ApprovedAt')}
										</TableHead>
										<TableHead className="min-w-[160px] whitespace-nowrap bg-gray-50">
											{t('OpenCurrentBalance')}
										</TableHead>
										<TableHead className="min-w-[160px] whitespace-nowrap bg-gray-50">
											{t('OpenOverdueBalance')}
										</TableHead>
										<TableHead className="min-w-[180px] whitespace-nowrap bg-gray-50">
											{t('ClosedMaxBalance')}
										</TableHead>
										<TableHead className="min-w-[140px] whitespace-nowrap bg-gray-50">
											{t('MonthlyPayment')}
										</TableHead>
										<TableHead className="min-w-[140px] whitespace-nowrap bg-gray-50">
											{t('DefaultScore')}
										</TableHead>
										<TableHead className="min-w-[100px] whitespace-nowrap bg-gray-50">
											{t('TotalTrials')}
										</TableHead>
										<TableHead className="min-w-[140px] whitespace-nowrap bg-gray-50">
											{t('MonthsOverdue')}
										</TableHead>
										<TableHead className="min-w-[160px] whitespace-nowrap bg-gray-50">
											{t('YearsInBureau')}
										</TableHead>
										<TableHead className="min-w-[180px] whitespace-nowrap bg-gray-50">
											{t('UtilizationPrePandemic')}
										</TableHead>
										<TableHead className="min-w-[140px] whitespace-nowrap bg-gray-50">
											{t('LastSixMonthsInquiries')}
										</TableHead>
										<TableHead className="min-w-[180px] whitespace-nowrap bg-gray-50">
											{t('UtilizationPostPandemic')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{customers.map((customer, idx) => (
										<TableRow key={idx}>
											<TableCell className="font-mono text-xs whitespace-nowrap">
												{customer.client_id}
											</TableCell>
											<TableCell className="whitespace-nowrap">{customer.phone_number}</TableCell>
											<TableCell className="max-w-[200px] truncate">
												{renderCustomerName(customer)}
											</TableCell>
											<TableCell className="text-xs whitespace-nowrap">
												{customer.email || '-'}
											</TableCell>
											<TableCell className="text-xs whitespace-nowrap">
												{formatDateTime(customer.started_at || '', 'es') || '-'}
											</TableCell>
											<TableCell className="text-xs whitespace-nowrap">
												{formatDateTime(customer.approved_at || '', 'es') || '-'}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatCurrency(customer.open_current_balance)}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatCurrency(customer.open_overdue_balance)}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatCurrency(customer.closed_maximum_balance)}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatCurrency(customer.monthly_payment)}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatDecimal(customer.credit_bureau_default_score)}
											</TableCell>
											<TableCell className="text-center whitespace-nowrap">
												{customer.total_trials ?? '-'}
											</TableCell>
											<TableCell className="text-center whitespace-nowrap">
												{customer.months_overdue ?? '-'}
											</TableCell>
											<TableCell className="text-center whitespace-nowrap">
												{customer.years_in_credit_bureau ?? '-'}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatDecimal(customer.utilization_pre_pandemic)}
											</TableCell>
											<TableCell className="text-center whitespace-nowrap">
												{customer.last_six_months_inquiries ?? '-'}
											</TableCell>
											<TableCell className="text-right whitespace-nowrap">
												{formatDecimal(customer.utilization_post_pandemic)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return <div className="mb-4">{tableContent}</div>;
};

export default CustomersPreviewTable;
